import React, { useState, useEffect } from "react";
import { FaShoppingBag, FaShoppingCart } from "react-icons/fa";
import { FaStore } from "react-icons/fa6";
import { FiPackage } from "react-icons/fi";

const Loader = ({ loading = true }) => {
  const [, setCurrentStep] = useState(0);
  const [, setProgress] = useState(0);
  const [, setShowLongMessage] = useState(false);

  const steps = [
    { icon: FaStore, text: "Connecting to store", color: "text-blue-500" },
    { icon: FaShoppingBag, text: "Loading products", color: "text-purple-500" },
    { icon: FiPackage, text: "Organizing catalog", color: "text-green-500" },
    { icon: FaShoppingCart, text: "Preparing cart", color: "text-orange-500" },
  ];

  useEffect(() => {
    if (loading) {
      const longMessageTimer = setTimeout(() => {
        setShowLongMessage(true);
      }, 8000);

      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) return 0;
          return prev + 2;
        });
      }, 80);

      const stepInterval = setInterval(() => {
        setCurrentStep((prev) => (prev + 1) % steps.length);
      }, 1500);

      return () => {
        clearTimeout(longMessageTimer);
        clearInterval(progressInterval);
        clearInterval(stepInterval);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (!loading) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-slate-50/50 to-blue-50/50 dark:from-gray-900/50 dark:to-blue-900/20 backdrop-blur">
      <div className="relative p-12 max-w-md w-full mx-4">
        <div className="flex flex-col items-center space-y-8">
          <div className="preloader">
            <svg
              className="cart"
              role="img"
              aria-label="Shopping cart line animation"
              viewBox="0 0 128 128"
              width="128px"
              height="128px"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="8"
              >
                <g className="cart__track" stroke="hsla(0,10%,10%,0.1)">
                  <polyline points="4,4 21,4 26,22 124,22 112,64 35,64 39,80 106,80" />
                  <circle cx="43" cy="111" r="13" />
                  <circle cx="102" cy="111" r="13" />
                </g>
                <g className="cart__lines" stroke="currentColor">
                  <polyline
                    className="cart__top"
                    points="4,4 21,4 26,22 124,22 112,64 35,64 39,80 106,80"
                    strokeDasharray="338 338"
                    strokeDashoffset="-338"
                  />
                  <g className="cart__wheel1" transform="rotate(-90,43,111)">
                    <circle
                      className="cart__wheel-stroke"
                      cx="43"
                      cy="111"
                      r="13"
                      strokeDasharray="81.68 81.68"
                      strokeDashoffset="81.68"
                    />
                  </g>
                  <g className="cart__wheel2" transform="rotate(90,102,111)">
                    <circle
                      className="cart__wheel-stroke"
                      cx="102"
                      cy="111"
                      r="13"
                      strokeDasharray="81.68 81.68"
                      strokeDashoffset="81.68"
                    />
                  </g>
                </g>
              </g>
            </svg>
          </div>
        </div>
      </div>

      <style jsx global>{`
        .preloader {
          text-align: center;
          max-width: 20em;
          width: 100%;
          margin-bottom: 1.5em;
        }
        .preloader__text {
          position: relative;
          height: 1.5em;
          color: white;
          margin-top: 1rem;
        }
        .preloader__msg {
          position: absolute;
          width: 100%;
          transition: opacity 0.3s ease, visibility 0.3s ease;
          left: 0;
        }
        .cart {
          display: block;
          margin: 0 auto;
          width: 8em;
          height: 8em;
        }
        .cart__lines,
        .cart__top,
        .cart__wheel1,
        .cart__wheel2,
        .cart__wheel-stroke {
          animation: cartLines 2s ease-in-out infinite;
        }
        .cart__lines {
          stroke: #85325f;
        }
        .cart__top {
          animation-name: cartTop;
        }
        .cart__wheel1 {
          animation-name: cartWheel1;
          transform: rotate(-0.25turn);
          transform-origin: 43px 111px;
        }
        .cart__wheel2 {
          animation-name: cartWheel2;
          transform: rotate(0.25turn);
          transform-origin: 102px 111px;
        }
        .cart__wheel-stroke {
          animation-name: cartWheelStroke;
        }
        .cart__track {
          stroke: rgba(255, 255, 255, 0.1);
          transition: stroke 0.3s;
        }

        @keyframes cartLines {
          from,
          to {
            opacity: 0;
          }
          8%,
          92% {
            opacity: 1;
          }
        }
        @keyframes cartTop {
          from {
            stroke-dashoffset: -338;
          }
          50% {
            stroke-dashoffset: 0;
          }
          to {
            stroke-dashoffset: 338;
          }
        }
        @keyframes cartWheel1 {
          from {
            transform: rotate(-0.25turn);
          }
          to {
            transform: rotate(2.75turn);
          }
        }
        @keyframes cartWheel2 {
          from {
            transform: rotate(0.25turn);
          }
          to {
            transform: rotate(3.25turn);
          }
        }
        @keyframes cartWheelStroke {
          from,
          to {
            stroke-dashoffset: 81.68;
          }
          50% {
            stroke-dashoffset: 40.84;
          }
        }
      `}</style>
    </div>
  );
};

export default React.memo(Loader);
